import React, { useCallback, useEffect, useRef, useState } from 'react';
import NemesisFace from './NemesisFace.jsx';
import ContentRenderer from './ContentRenderer.jsx';
import { authApiIdempotent } from '../firebase/config.js';
import { ENV } from '../config/env.js';

const API_BASE_URL = ENV.apiBaseUrl;

// ── Difficulty config ──────────────────────────────────────────────────────────
const DIFF = {
    easy:   { timerMs: 15000, maxDmg: 18, healWrong: 5,  healTimeout: 3,  shields: 4, label: 'Easy' },
    normal: { timerMs: 10000, maxDmg: 22, healWrong: 9,  healTimeout: 5,  shields: 3, label: 'Normal' },
    hard:   { timerMs: 6000,  maxDmg: 28, healWrong: 15, healTimeout: 9,  shields: 2, label: 'Hard' },
};

const calcMaxHp = (deckLen, d) => Math.max(30, Math.min(100, Math.floor(deckLen * d.maxDmg * 0.78)));

// ── Banter banks ──────────────────────────────────────────────────────────────
const BANTER = {
    game_start: [
        "Another challenger. How refreshing. How futile.",
        "I'll try not to fall asleep before you do.",
        "Oh good. You remembered how to open an app. Encouraging start.",
        "I've seen better recall from goldfish. Impress me.",
        "Begin. Don't embarrass yourself immediately.",
    ],
    correct: [
        "Fine. That one was correct. Don't let it go to your head.",
        "Beginner's luck. We'll see how long that lasts.",
        "Correct. You've cleared the minimum threshold of sentience.",
        "One point for the human. Marginally impressive.",
        "You knew that one. Good for you. Truly.",
        "Acceptable. Move on before I change my mind about you.",
    ],
    partial: [
        "Close. Not close enough, but close.",
        "You almost had it. Almost is where failure goes to live.",
        "Partial credit. The participation trophy of trivia.",
        "That was somewhere between right and catastrophically wrong.",
    ],
    wrong: [
        "Incredible. Somehow worse than silence.",
        "That wasn't even close. Were you guessing or is this genuinely your limit?",
        "I almost feel bad. Almost.",
        "That answer was a gift. To me.",
        "Mmm. No.",
        "Wrong. Definitively, embarrassingly, classically wrong.",
        "I'd say try harder, but I'm not sure that's the issue here.",
    ],
    timeout: [
        "Time. And silence. The answer was neither.",
        "The clock spoke. You didn't.",
        "Patience depleted. Much like your recall, apparently.",
        "I waited. You failed to show up mentally.",
        "Nothing. You gave me nothing. Noted.",
    ],
    streak_3: [
        "Three in a row. Noted. I'm adjusting my expectations.",
        "A streak. How... alarming.",
        "You've been studying. I can tell. I don't like it.",
    ],
    streak_5: [
        "Five in a row. This is a problem for me.",
        "Alright. You want to do this the hard way. Fine.",
        "I'm going to need you to make a mistake. Preferably soon.",
    ],
    hp_low: [
        "You're... actually not terrible. That's unfortunate for me.",
        "Fine. I'll acknowledge you're a threat. A minor, irritating one.",
        "I'm going to need you to stop doing that.",
        "This was not the expected outcome. I'm recalibrating.",
    ],
    hp_critical: [
        "This is not how this was supposed to go.",
        "Every correct answer you give physically hurts. I hope you know that.",
        "I'm updating my threat assessment. You're now a problem.",
        "Fine. Fine. Just... fine.",
    ],
    nemesis_wins: [
        "And that's why you study. Or rather — why you didn't.",
        "I want you to sit with this moment. Absorb it.",
        "Better luck with a different deck. Or a different brain.",
        "I won. You knew this was coming.",
        "The score speaks for itself. I'll let it.",
    ],
    player_wins: [
        "Well. You've managed to defeat me. I'll pretend this never happened.",
        "Don't celebrate. I let you win.",
        "This is temporary. I have other decks.",
        "Fine. You win. This specific time. It won't happen again.",
        "Impressive. Irritating, but impressive.",
        "I underestimated you. A mistake I won't make again.",
    ],
};

function getLine(ctx, playerName = '') {
    const bank = BANTER[ctx] || [];
    if (!bank.length) return '';
    const line = bank[Math.floor(Math.random() * bank.length)];
    return playerName && Math.random() > 0.55 ? line.replace(/\bYou\b/g, playerName).replace(/\byou\b/g, playerName) : line;
}

// ── Sub-components ────────────────────────────────────────────────────────────
const Shield = ({ active }) => (
    <svg className={`h-5 w-5 transition-all duration-300 ${active ? 'text-emerald-400 drop-shadow-[0_0_4px_rgba(52,211,153,0.8)]' : 'text-slate-700'}`}
        fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2L3 7v5c0 5.25 3.75 10.15 9 11.35C17.25 22.15 21 17.25 21 12V7l-9-5z" />
    </svg>
);

const PatienceMeter = ({ remaining, total }) => {
    const pct = total > 0 ? Math.max(0, (remaining / total)) * 100 : 0;
    const color = pct > 55 ? 'from-emerald-500 to-teal-400' : pct > 28 ? 'from-amber-400 to-yellow-500' : 'from-rose-500 to-red-400';
    return (
        <div className="w-full h-1.5 rounded-full bg-white/8 overflow-hidden">
            <div className={`h-full rounded-full bg-gradient-to-r ${color} transition-none`} style={{ width: `${pct}%` }} />
        </div>
    );
};

// ── Main game ─────────────────────────────────────────────────────────────────
export default function NemesisGame({ folder, onClose }) {
    const [deck] = useState(() => [...folder.cards].sort(() => Math.random() - 0.5));
    const [difficulty, setDifficulty] = useState('normal');
    const [inputMode, setInputMode] = useState('type');
    const [playerName, setPlayerName] = useState('');

    // Game flow
    const [gameState, setGameState] = useState('landing');
    const [countdownNum, setCountdownNum] = useState(3);
    const [currentIndex, setCurrentIndex] = useState(0);

    // Timer
    const [timerMs, setTimerMs] = useState(10000);
    const [totalTimerMs, setTotalTimerMs] = useState(10000);

    // Nemesis / player state
    const [nemesisHp, setNemesisHp] = useState(100);
    const [nemesisMaxHp, setNemesisMaxHp] = useState(100);
    const [shields, setShields] = useState(3);
    const [streak, setStreak] = useState(0);
    const [score, setScore] = useState(0);
    const [nemesisState, setNemesisState] = useState('idle');

    // Result / banter
    const [typedAnswer, setTypedAnswer] = useState('');
    const [voiceTranscript, setVoiceTranscript] = useState('');
    const [lastResult, setLastResult] = useState(null);
    const [banterDisplayed, setBanterDisplayed] = useState('');
    const [history, setHistory] = useState([]);

    // End state
    const [gameResult, setGameResult] = useState(null);
    const [finalScore, setFinalScore] = useState(0);
    const [showHistory, setShowHistory] = useState(false);

    // TTS voices
    const [nemesisVoice, setNemesisVoice] = useState(null);

    // Refs for async-safe reads of current state values
    const nemesisHpRef = useRef(100);
    const shieldsRef = useRef(3);
    const streakRef = useRef(0);
    const scoreRef = useRef(0);
    const nemesisMaxHpRef = useRef(100);

    // Keep refs in sync
    useEffect(() => { nemesisHpRef.current = nemesisHp; }, [nemesisHp]);
    useEffect(() => { shieldsRef.current = shields; }, [shields]);
    useEffect(() => { streakRef.current = streak; }, [streak]);
    useEffect(() => { scoreRef.current = score; }, [score]);
    useEffect(() => { nemesisMaxHpRef.current = nemesisMaxHp; }, [nemesisMaxHp]);

    // Interval refs
    const timerRef = useRef(null);
    const banterRef = useRef(null);
    const recognitionRef = useRef(null);
    const transcriptRef = useRef('');
    const typedInputRef = useRef(null);
    const submitAnswerRef = useRef(null);
    const autoAdvanceRef = useRef(null);
    const questionStartRef = useRef(Date.now());
    const currentIndexRef = useRef(0);
    useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);

    const diff = DIFF[difficulty];
    const currentCard = deck[currentIndex];
    const hasSpeechRec = typeof window !== 'undefined' && !!(window.SpeechRecognition || window.webkitSpeechRecognition);

    // ── TTS voices ─────────────────────────────────────────────────────────────
    useEffect(() => {
        const load = () => {
            const en = window.speechSynthesis.getVoices().filter(v => v.lang.startsWith('en'));
            const preferred = en.find(v => /david|daniel|alex|james|rishi|arthur/i.test(v.name)) || en[0];
            if (preferred) setNemesisVoice(preferred);
        };
        window.speechSynthesis.onvoiceschanged = load;
        load();
        return () => { window.speechSynthesis.onvoiceschanged = null; };
    }, []);

    // ── TTS speak ──────────────────────────────────────────────────────────────
    const speak = useCallback((text, onEnd, { rate = 0.88, pitch = 0.82 } = {}) => {
        window.speechSynthesis.cancel();
        if (!text) { if (onEnd) setTimeout(onEnd, 100); return; }
        const utt = new SpeechSynthesisUtterance(text);
        if (nemesisVoice) utt.voice = nemesisVoice;
        utt.rate = rate;
        utt.pitch = pitch;
        utt.onend = onEnd || null;
        utt.onerror = () => { if (onEnd) onEnd(); };
        window.speechSynthesis.speak(utt);
    }, [nemesisVoice]);

    // ── Banter typewriter ──────────────────────────────────────────────────────
    const showBanter = useCallback((line, onDone) => {
        clearInterval(banterRef.current);
        setBanterDisplayed('');
        if (!line) { if (onDone) onDone(); return; }
        let i = 0;
        banterRef.current = setInterval(() => {
            i++;
            setBanterDisplayed(line.slice(0, i));
            if (i >= line.length) {
                clearInterval(banterRef.current);
                if (onDone) setTimeout(onDone, 500);
            }
        }, 26);
    }, []);

    // ── Timer ──────────────────────────────────────────────────────────────────
    const stopTimer = () => { clearInterval(timerRef.current); timerRef.current = null; };

    const startTimer = useCallback((ms, onExpire) => {
        stopTimer();
        setTimerMs(ms);
        setTotalTimerMs(ms);
        let remaining = ms;
        timerRef.current = setInterval(() => {
            remaining -= 50;
            setTimerMs(Math.max(0, remaining));
            if (remaining <= 0) { stopTimer(); onExpire(); }
        }, 50);
    }, []);

    // ── Voice recognition ──────────────────────────────────────────────────────
    const startRecognition = () => {
        if (!hasSpeechRec) return;
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const rec = new SR();
        rec.continuous = true;
        rec.interimResults = true;
        rec.lang = 'en-US';
        transcriptRef.current = '';
        rec.onresult = (e) => {
            let t = '';
            for (const r of e.results) t += r[0].transcript;
            transcriptRef.current = t;
            setVoiceTranscript(t);
        };
        rec.onerror = () => {};
        rec.start();
        recognitionRef.current = rec;
    };

    const stopRecognition = () => {
        try { recognitionRef.current?.stop(); } catch {}
        recognitionRef.current = null;
    };

    // ── Core scoring logic (pure, reads from refs) ─────────────────────────────
    const processResult = useCallback((aiScore, elapsedMs) => {
        const d = DIFF[difficulty];
        const maxHp = nemesisMaxHpRef.current;
        const currentHp = nemesisHpRef.current;
        const currentShields = shieldsRef.current;
        const currentStreak = streakRef.current;
        const currentScore = scoreRef.current;

        const isCorrect = aiScore >= 70;
        const isPartial = aiScore >= 30 && aiScore < 70;
        const speedBonus = elapsedMs < d.timerMs * 0.4 ? 3 : 0;
        const streakBonus = currentStreak >= 5 ? 4 : currentStreak >= 3 ? 2 : 0;

        let damage = 0, heal = 0, newStreak = currentStreak, newShields = currentShields, banterCtx = 'wrong';

        if (isCorrect) {
            damage = Math.round((aiScore / 100) * d.maxDmg) + speedBonus + streakBonus;
            newStreak = currentStreak + 1;
            banterCtx = 'correct';
        } else if (isPartial) {
            damage = Math.round((aiScore / 100) * d.maxDmg * 0.45);
            newStreak = 0;
            banterCtx = 'partial';
        } else {
            heal = d.healWrong;
            newStreak = 0;
            newShields = Math.max(0, currentShields - 1);
            banterCtx = 'wrong';
        }

        if (isCorrect && newStreak === 5) banterCtx = 'streak_5';
        else if (isCorrect && newStreak === 3) banterCtx = 'streak_3';

        const newHp = Math.max(0, Math.min(maxHp, currentHp - damage + heal));
        if (damage > 0) {
            if (newHp < maxHp * 0.2) banterCtx = 'hp_critical';
            else if (newHp < maxHp * 0.4) banterCtx = 'hp_low';
        }
        // Don't override streak-specific banter
        if (banterCtx === 'correct' && (newStreak === 3 || newStreak === 5)) {
            banterCtx = newStreak === 5 ? 'streak_5' : 'streak_3';
        }

        const banterLine = getLine(banterCtx, playerName);
        const newTotalScore = currentScore + damage;

        return { aiScore, damage, heal, newHp, newStreak, newShields, banterLine, isCorrect, isPartial, speedBonus, streakBonus, newTotalScore };
    }, [difficulty, playerName]);

    // ── End game ───────────────────────────────────────────────────────────────
    const endGame = useCallback((victory, fs) => {
        stopTimer();
        window.speechSynthesis.cancel();
        stopRecognition();
        clearTimeout(autoAdvanceRef.current);
        clearInterval(banterRef.current);

        setFinalScore(fs);
        setGameResult(victory ? 'victory' : 'defeat');
        setNemesisState(victory ? 'dead' : 'winning');

        const banterLine = getLine(victory ? 'player_wins' : 'nemesis_wins', playerName);
        setGameState('game_over');
        setBanterDisplayed('');
        showBanter(banterLine);
        speak(banterLine, null, { rate: 0.85, pitch: victory ? 0.78 : 0.88 });
    }, [playerName, showBanter, speak]);

    // ── Submit answer (reads from refs, no stale closures) ────────────────────
    const submitAnswer = useCallback(async (rawText) => {
        stopTimer();
        stopRecognition();
        const text = (rawText || '').trim();
        const elapsed = Date.now() - questionStartRef.current;
        setGameState('scoring');
        setNemesisState('idle');

        let aiScore = 0;
        if (text) {
            try {
                const data = await authApiIdempotent(`${API_BASE_URL}/score-answer`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userAnswer: text, correctAnswer: currentCard?.answer }),
                });
                aiScore = Number(data.score ?? 0);
            } catch { aiScore = 0; }
        }

        const result = processResult(aiScore, elapsed);

        // Batch all state updates
        setNemesisHp(result.newHp);
        setShields(result.newShields);
        setStreak(result.newStreak);
        setScore(result.newTotalScore);
        setLastResult({ ...result, userAnswer: text });
        setHistory(prev => [...prev, {
            card: currentCard, userAnswer: text, aiScore,
            pointsGained: result.damage, isCorrect: result.isCorrect,
            isPartial: result.isPartial,
        }]);

        // Face reaction
        if (result.isCorrect) {
            setNemesisState('damaged');
            setTimeout(() => setNemesisState(result.newHp < nemesisMaxHpRef.current * 0.2 ? 'critical' : 'idle'), 550);
        } else {
            setNemesisState('winning');
            setTimeout(() => setNemesisState(result.newHp < nemesisMaxHpRef.current * 0.2 ? 'critical' : 'idle'), 1200);
        }

        // Win/lose check
        const isVictory = result.newHp <= 0;
        const isDefeat = result.newShields <= 0;
        const isLastCard = currentIndexRef.current >= deck.length - 1;

        setGameState('reveal');
        setBanterDisplayed('');
        showBanter(result.banterLine);
        speak(result.banterLine, () => {
            clearTimeout(autoAdvanceRef.current);
            autoAdvanceRef.current = setTimeout(() => {
                if (isVictory) { endGame(true, result.newTotalScore); return; }
                if (isDefeat) { endGame(false, result.newTotalScore); return; }
                if (isLastCard) { endGame(false, result.newTotalScore); return; }
                advanceToNext();
            }, 1500);
        });
    }, [currentCard, deck.length, endGame, processResult, showBanter, speak]);

    useEffect(() => { submitAnswerRef.current = submitAnswer; }, [submitAnswer]);

    // ── Timeout ────────────────────────────────────────────────────────────────
    const handleTimeout = useCallback(() => {
        stopRecognition();
        const text = transcriptRef.current || '';
        const d = DIFF[difficulty];
        const currentHp = nemesisHpRef.current;
        const maxHp = nemesisMaxHpRef.current;
        const currentScore = scoreRef.current;
        const currentShields = shieldsRef.current;

        const newHp = Math.min(maxHp, currentHp + d.healTimeout);
        const banterLine = getLine('timeout', playerName);

        setNemesisHp(newHp);
        setStreak(0);
        setLastResult({ aiScore: 0, damage: 0, heal: d.healTimeout, newHp, newShields: currentShields, banterLine, isCorrect: false, isPartial: false, userAnswer: text, isTimeout: true, newTotalScore: currentScore, speedBonus: 0, streakBonus: 0 });
        setHistory(prev => [...prev, { card: currentCard, userAnswer: text, aiScore: 0, pointsGained: 0, isCorrect: false, isPartial: false, isTimeout: true }]);
        setNemesisState('winning');
        setTimeout(() => setNemesisState(newHp >= maxHp * 0.85 ? 'winning' : 'idle'), 1200);

        const isLastCard = currentIndexRef.current >= deck.length - 1;
        setGameState('reveal');
        setBanterDisplayed('');
        showBanter(banterLine);
        speak(banterLine, () => {
            clearTimeout(autoAdvanceRef.current);
            autoAdvanceRef.current = setTimeout(() => {
                if (isLastCard) { endGame(newHp <= 0, currentScore); return; }
                advanceToNext();
            }, 1500);
        });
    }, [currentCard, deck.length, difficulty, endGame, playerName, showBanter, speak]);

    // ── Advance card ───────────────────────────────────────────────────────────
    const advanceToNext = useCallback(() => {
        clearTimeout(autoAdvanceRef.current);
        stopTimer();
        setTypedAnswer('');
        setVoiceTranscript('');
        transcriptRef.current = '';
        setLastResult(null);
        setBanterDisplayed('');
        setCurrentIndex(i => i + 1);
        setGameState('question');
    }, []);

    // ── Question effect ────────────────────────────────────────────────────────
    useEffect(() => {
        if (gameState !== 'question' || !currentCard) return;
        setNemesisState(nemesisHpRef.current < nemesisMaxHpRef.current * 0.2 ? 'critical' : 'idle');
        transcriptRef.current = '';
        setVoiceTranscript('');
        questionStartRef.current = Date.now();

        const d = DIFF[difficulty];

        const startPlay = () => {
            if (inputMode === 'voice' && hasSpeechRec) startRecognition();
            else setTimeout(() => typedInputRef.current?.focus(), 80);
            startTimer(d.timerMs, () => submitAnswerRef.current?.(transcriptRef.current));
        };

        setNemesisState('speaking');
        speak(`Question. ${currentCard.question}`, () => {
            setNemesisState(nemesisHpRef.current < nemesisMaxHpRef.current * 0.2 ? 'critical' : 'idle');
            startPlay();
        }, { rate: 0.92, pitch: 0.8 });

        return () => { stopTimer(); window.speechSynthesis.cancel(); stopRecognition(); clearTimeout(autoAdvanceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameState, currentIndex]);

    // ── Cleanup ────────────────────────────────────────────────────────────────
    useEffect(() => () => {
        stopTimer();
        window.speechSynthesis.cancel();
        stopRecognition();
        clearTimeout(autoAdvanceRef.current);
        clearInterval(banterRef.current);
    }, []);

    // ── Manual next on reveal ──────────────────────────────────────────────────
    const handleManualNext = () => {
        clearTimeout(autoAdvanceRef.current);
        if (!lastResult) return;
        const isVictory = lastResult.newHp <= 0;
        const isDefeat = lastResult.newShields <= 0;
        const isLastCard = currentIndex >= deck.length - 1;
        if (isVictory) { endGame(true, lastResult.newTotalScore); return; }
        if (isDefeat) { endGame(false, lastResult.newTotalScore); return; }
        if (isLastCard) { endGame(false, lastResult.newTotalScore); return; }
        advanceToNext();
    };

    // ── Start countdown ────────────────────────────────────────────────────────
    const startCountdown = () => {
        const d = DIFF[difficulty];
        const maxHp = calcMaxHp(deck.length, d);
        setNemesisMaxHp(maxHp);
        setNemesisHp(maxHp);
        nemesisMaxHpRef.current = maxHp;
        nemesisHpRef.current = maxHp;
        setShields(d.shields);
        shieldsRef.current = d.shields;
        setStreak(0); streakRef.current = 0;
        setScore(0); scoreRef.current = 0;
        setHistory([]);
        setCurrentIndex(0); currentIndexRef.current = 0;
        setLastResult(null);
        setBanterDisplayed('');
        setGameResult(null);
        setNemesisState('idle');

        let n = 3;
        setCountdownNum(n);
        setGameState('countdown');

        const line = getLine('game_start', playerName);
        speak(line, () => {
            showBanter('', () => {
                const iv = setInterval(() => {
                    n--;
                    setCountdownNum(n);
                    if (n <= 0) {
                        clearInterval(iv);
                        setBanterDisplayed('');
                        setCurrentIndex(0);
                        setGameState('question');
                    }
                }, 900);
            });
        }, { rate: 0.88, pitch: 0.8 });
        showBanter(line);
    };

    // ── Shared renders ─────────────────────────────────────────────────────────
    const renderHpBar = () => {
        const pct = nemesisMaxHp > 0 ? Math.max(0, nemesisHp / nemesisMaxHp) * 100 : 0;
        const color = pct > 60 ? 'from-emerald-500 to-teal-500' : pct > 25 ? 'from-amber-500 to-orange-400' : 'from-rose-600 to-red-500';
        return (
            <div className="w-full">
                <div className="flex items-center justify-between mb-1">
                    <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Nemesis HP</span>
                    <span className="text-[10px] font-semibold tabular-nums text-slate-400">{nemesisHp}/{nemesisMaxHp}</span>
                </div>
                <div className="h-2 rounded-full bg-white/8 overflow-hidden">
                    <div className={`h-full rounded-full bg-gradient-to-r ${color} transition-all duration-400`} style={{ width: `${pct}%` }} />
                </div>
            </div>
        );
    };

    const renderStatusBar = () => (
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/8 bg-slate-950/60">
            <div className="flex items-center gap-1">
                {Array.from({ length: DIFF[difficulty].shields }).map((_, i) => (
                    <Shield key={i} active={i < shields} />
                ))}
            </div>
            <div className="flex items-center gap-3">
                {streak >= 3 && (
                    <span className="text-[10px] font-bold text-amber-300 bg-amber-500/15 rounded-full px-2 py-0.5">
                        🔥 {streak}
                    </span>
                )}
                <span className="text-xs font-semibold tabular-nums text-white">{score} pts</span>
                <span className="text-[10px] text-slate-500">{currentIndex + 1}/{deck.length}</span>
            </div>
        </div>
    );

    const renderBanter = (minimal = false) => {
        if (!banterDisplayed) return null;
        return (
            <div className={`mx-auto max-w-sm rounded-xl border border-white/8 bg-slate-900/80 px-4 py-2.5 ${minimal ? 'text-xs' : 'text-sm'} text-slate-200 italic leading-relaxed`}>
                <span className="not-italic text-[9px] font-semibold uppercase tracking-widest text-emerald-400/70 mr-1.5">NEMESIS</span>
                "{banterDisplayed}<span className="inline-block w-0.5 h-3 bg-emerald-400 animate-pulse align-middle ml-0.5" />"
            </div>
        );
    };

    // ══════════════════════════════════════════════════════════════════════════
    // RENDER STATES
    // ══════════════════════════════════════════════════════════════════════════

    // ── Landing ───────────────────────────────────────────────────────────────
    if (gameState === 'landing') return (
        <div className="fixed inset-0 z-50 bg-slate-950 flex items-center justify-center px-4 overflow-y-auto">
            <div className="w-full max-w-sm space-y-5 py-8">
                <div className="text-center space-y-2">
                    <NemesisFace hp={100} maxHp={100} nemesisState="idle" size={150} className="mx-auto" />
                    <h1 className="text-3xl font-black tracking-tight text-white mt-3" style={{ letterSpacing: '-0.03em' }}>
                        ANAMNESIS<br /><span className="text-emerald-400">NEMESIS</span>
                    </h1>
                    <p className="text-sm text-slate-400">{folder.name} · {deck.length} cards</p>
                </div>

                <div>
                    <label className="block text-xs text-slate-400 mb-1.5">Your name (optional)</label>
                    <input value={playerName} onChange={e => setPlayerName(e.target.value)}
                        placeholder="Anonymous Challenger"
                        className="w-full rounded-xl bg-slate-800/60 border border-white/10 px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40" />
                </div>

                <div>
                    <label className="block text-xs text-slate-400 mb-1.5">Difficulty</label>
                    <div className="rounded-xl bg-white/5 p-1 flex gap-1">
                        {['easy', 'normal', 'hard'].map(d => (
                            <button key={d} onClick={() => setDifficulty(d)}
                                className={`flex-1 rounded-lg py-2 text-sm font-medium capitalize transition ${difficulty === d ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
                                {DIFF[d].label}
                            </button>
                        ))}
                    </div>
                    <p className="text-[10px] text-slate-600 mt-1.5 text-center">
                        {diff.timerMs / 1000}s per question · {diff.shields} shields · up to {diff.maxDmg} dmg/card
                    </p>
                </div>

                <div>
                    <label className="block text-xs text-slate-400 mb-1.5">Answer Mode</label>
                    <div className="rounded-xl bg-white/5 p-1 flex gap-1">
                        {[['type', '⌨ Type'], ['voice', '🎙 Voice']].map(([v, l]) => (
                            <button key={v} onClick={() => setInputMode(v)}
                                disabled={v === 'voice' && !hasSpeechRec}
                                className={`flex-1 rounded-lg py-2 text-sm font-medium transition disabled:opacity-30 ${inputMode === v ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
                                {l}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="space-y-2 pt-1">
                    <button onClick={startCountdown}
                        className="w-full rounded-xl py-3.5 text-base font-black uppercase tracking-wide text-white bg-gradient-to-r from-emerald-700 to-teal-700 hover:opacity-90 shadow-xl shadow-emerald-700/25 transition active:scale-[0.98]">
                        Face the Nemesis
                    </button>
                    <button onClick={onClose} className="w-full rounded-xl py-2.5 text-sm text-slate-400 hover:text-white transition">
                        ← Back
                    </button>
                </div>
            </div>
        </div>
    );

    // ── Countdown ─────────────────────────────────────────────────────────────
    if (gameState === 'countdown') return (
        <div className="fixed inset-0 z-50 bg-slate-950 flex flex-col items-center justify-center gap-6">
            <NemesisFace hp={nemesisMaxHp} maxHp={nemesisMaxHp} nemesisState="speaking" size={130} />
            {banterDisplayed ? renderBanter() : (
                <div className="text-center">
                    <div className="text-8xl font-black text-white tabular-nums"
                        style={{ textShadow: '0 0 40px rgba(52,211,153,0.6)' }}>
                        {countdownNum > 0 ? countdownNum : '!'}
                    </div>
                    <p className="text-slate-400 text-sm mt-2 uppercase tracking-widest">
                        {countdownNum > 0 ? 'Get ready…' : 'BEGIN'}
                    </p>
                </div>
            )}
        </div>
    );

    // ── Question + Scoring ────────────────────────────────────────────────────
    if (gameState === 'question' || gameState === 'scoring') return (
        <div className="fixed inset-0 z-50 bg-slate-950 flex flex-col">
            {renderStatusBar()}

            <div className="flex items-center gap-4 px-4 pt-4 pb-3">
                <NemesisFace hp={nemesisHp} maxHp={nemesisMaxHp} nemesisState={nemesisState} size={72} />
                <div className="flex-1">{renderHpBar()}</div>
            </div>

            <div className="px-4 min-h-[44px]">{renderBanter(true)}</div>

            <div className="flex-1 overflow-y-auto px-4 pt-2 pb-6 space-y-4">
                <div className="rounded-2xl border border-white/10 bg-slate-900/80 p-5 shadow-lg">
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-emerald-400/70 mb-2">
                        Question {currentIndex + 1}
                    </p>
                    <p className="text-white text-base leading-relaxed">
                        <ContentRenderer content={currentCard?.question} />
                    </p>
                </div>

                <div className="space-y-1">
                    <div className="flex justify-between items-center">
                        <span className="text-[9px] uppercase tracking-widest text-slate-600">Nemesis Patience</span>
                        <span className="text-[10px] text-slate-500 tabular-nums">{Math.ceil(timerMs / 1000)}s</span>
                    </div>
                    <PatienceMeter remaining={timerMs} total={totalTimerMs} />
                </div>

                {gameState === 'scoring' ? (
                    <div className="flex items-center justify-center gap-3 py-6">
                        <div className="h-5 w-5 rounded-full border-2 border-emerald-400/40 border-t-emerald-400 animate-spin" />
                        <span className="text-sm text-slate-400">Nemesis is judging…</span>
                    </div>
                ) : inputMode === 'type' ? (
                    <div className="space-y-2">
                        <textarea ref={typedInputRef} value={typedAnswer}
                            onChange={e => setTypedAnswer(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    if (typedAnswer.trim()) { stopTimer(); submitAnswer(typedAnswer); }
                                }
                            }}
                            rows={2}
                            placeholder="Type your answer… (Enter to submit)"
                            className="w-full rounded-xl bg-slate-800/60 border border-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 resize-none"
                        />
                        <button
                            onClick={() => { if (typedAnswer.trim()) { stopTimer(); submitAnswer(typedAnswer); } }}
                            disabled={!typedAnswer.trim()}
                            className="w-full rounded-xl py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-emerald-700 to-teal-700 hover:opacity-90 disabled:opacity-35 transition">
                            Submit
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <div className="flex items-center justify-center gap-3">
                            <div className={`h-4 w-4 rounded-full ${voiceTranscript ? 'bg-emerald-400' : 'bg-rose-500 animate-pulse'}`} />
                            <span className="text-sm text-slate-300">{voiceTranscript ? 'Heard you.' : 'Listening…'}</span>
                        </div>
                        {voiceTranscript && (
                            <div className="rounded-xl bg-white/5 border border-white/8 px-4 py-2.5">
                                <p className="text-[10px] text-slate-500 mb-1">Transcribing:</p>
                                <p className="text-sm text-slate-200">{voiceTranscript}</p>
                            </div>
                        )}
                        <button
                            onClick={() => { stopTimer(); submitAnswer(transcriptRef.current); }}
                            className="w-full rounded-xl py-2.5 text-sm font-medium text-slate-200 bg-slate-800 hover:bg-slate-700 ring-1 ring-white/10 transition">
                            Submit Now
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    // ── Reveal ────────────────────────────────────────────────────────────────
    if (gameState === 'reveal' && lastResult) {
        const { aiScore, damage, newHp, newShields, isCorrect, isPartial, isTimeout, userAnswer: ua, speedBonus, streakBonus } = lastResult;
        const label = isTimeout ? 'Time!' : isCorrect ? 'Correct!' : isPartial ? 'Partial' : 'Wrong';
        const labelColor = isCorrect ? 'text-emerald-400' : isPartial ? 'text-amber-400' : 'text-rose-400';
        const borderColor = isCorrect ? 'border-emerald-500/25 bg-emerald-500/6' : isPartial ? 'border-amber-500/25 bg-amber-500/6' : 'border-rose-500/25 bg-rose-500/6';

        return (
            <div className="fixed inset-0 z-50 bg-slate-950/92 backdrop-blur-sm flex flex-col items-center justify-center px-4 gap-4">
                <NemesisFace hp={newHp} maxHp={nemesisMaxHp} nemesisState={nemesisState} size={80} />

                <div className={`w-full max-w-md rounded-2xl border ${borderColor} shadow-2xl p-6 space-y-4`}>
                    <div className="text-center">
                        <div className={`text-5xl font-black tabular-nums ${labelColor}`}>
                            {damage > 0 ? `+${damage}` : '0'}
                        </div>
                        <p className={`text-lg font-bold mt-1 ${labelColor}`}>{label}</p>
                        {(speedBonus > 0 || streakBonus > 0) && (
                            <div className="flex items-center justify-center gap-2 mt-1.5">
                                {speedBonus > 0 && <span className="text-[10px] text-cyan-300 bg-cyan-500/15 rounded-full px-2 py-0.5">⚡ +{speedBonus} speed</span>}
                                {streakBonus > 0 && <span className="text-[10px] text-amber-300 bg-amber-500/15 rounded-full px-2 py-0.5">🔥 +{streakBonus} streak</span>}
                            </div>
                        )}
                    </div>

                    {ua && !isCorrect && (
                        <div className="rounded-xl bg-white/4 border border-white/8 px-4 py-2.5 space-y-1">
                            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">You said</p>
                            <p className="text-sm text-slate-300">{ua}</p>
                        </div>
                    )}
                    {!isCorrect && (
                        <div className="rounded-xl bg-white/4 border border-white/8 px-4 py-2.5 space-y-1">
                            <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-500">Correct answer</p>
                            <p className="text-sm text-white"><ContentRenderer content={currentCard?.answer} /></p>
                        </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-slate-500">
                        <span>Nemesis HP: <span className="text-slate-300 tabular-nums">{newHp}/{nemesisMaxHp}</span></span>
                        {newShields < DIFF[difficulty].shields && <span className="text-rose-400">Shield lost!</span>}
                    </div>

                    <button onClick={handleManualNext}
                        className={`w-full rounded-xl py-2.5 text-sm font-semibold text-white transition ${isCorrect ? 'bg-emerald-700 hover:bg-emerald-600' : isPartial ? 'bg-amber-700 hover:bg-amber-600' : 'bg-rose-700 hover:bg-rose-600'}`}>
                        {currentIndex + 1 >= deck.length || newHp <= 0 || newShields <= 0 ? 'See Result' : 'Next →'}
                    </button>
                </div>

                <div className="w-full max-w-md">{renderBanter()}</div>
            </div>
        );
    }

    // ── Game over ─────────────────────────────────────────────────────────────
    if (gameState === 'game_over') {
        const isVictory = gameResult === 'victory';
        const maxPossible = deck.length * diff.maxDmg;
        const pct = maxPossible > 0 ? Math.round((finalScore / maxPossible) * 100) : 0;
        const accuracy = history.length > 0 ? Math.round((history.filter(h => h.isCorrect).length / history.length) * 100) : 0;

        return (
            <div className="fixed inset-0 z-50 bg-slate-950 overflow-y-auto">
                <div className="min-h-full flex flex-col items-center px-4 py-8 gap-6 max-w-lg mx-auto">
                    <NemesisFace hp={isVictory ? 0 : nemesisHp} maxHp={nemesisMaxHp}
                        nemesisState={isVictory ? 'dead' : 'winning'} size={140} />

                    <div className="text-center">
                        <h2 className={`text-3xl font-black uppercase tracking-tight ${isVictory ? 'text-emerald-400' : 'text-rose-400'}`}
                            style={{ textShadow: isVictory ? '0 0 30px rgba(52,211,153,0.4)' : '0 0 30px rgba(248,113,113,0.4)' }}>
                            {isVictory ? 'Nemesis Defeated' : 'The Nemesis Wins'}
                        </h2>
                        <p className="text-slate-400 text-sm mt-1">{folder.name}</p>
                    </div>

                    {renderBanter()}

                    <div className="w-full rounded-2xl border border-white/10 bg-slate-900/70 p-5 space-y-3">
                        <div className="flex items-end justify-between">
                            <div>
                                <p className="text-3xl font-black text-white tabular-nums">{finalScore}</p>
                                <p className="text-xs text-slate-500">damage dealt</p>
                            </div>
                            <div className="text-right">
                                <p className={`text-2xl font-bold tabular-nums ${accuracy >= 70 ? 'text-emerald-400' : accuracy >= 40 ? 'text-amber-400' : 'text-rose-400'}`}>
                                    {accuracy}%
                                </p>
                                <p className="text-xs text-slate-500">accuracy</p>
                            </div>
                        </div>
                        <div className="h-2 rounded-full bg-white/8 overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-700 ${isVictory ? 'bg-gradient-to-r from-emerald-400 to-teal-400' : 'bg-gradient-to-r from-amber-500 to-orange-500'}`}
                                style={{ width: `${Math.min(100, pct)}%` }} />
                        </div>
                        <div className="grid grid-cols-3 gap-3 pt-1">
                            {[['Cards', `${history.length}/${deck.length}`], ['Streak', `${streak}`], ['Difficulty', DIFF[difficulty].label]].map(([k, v]) => (
                                <div key={k} className="text-center">
                                    <p className="text-sm font-bold text-white">{v}</p>
                                    <p className="text-[10px] text-slate-500">{k}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="w-full">
                        <button onClick={() => setShowHistory(h => !h)}
                            className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white/5 hover:bg-white/8 text-sm font-medium text-slate-200 transition">
                            <span>Review ({history.length} cards)</span>
                            <svg className={`h-4 w-4 transition-transform ${showHistory ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        {showHistory && (
                            <div className="mt-2 rounded-xl border border-white/8 overflow-hidden">
                                <div className="grid grid-cols-[1fr_1fr_auto] gap-2 px-3 py-2 bg-slate-900/60 text-[9px] font-semibold uppercase tracking-wider text-slate-500">
                                    <span>Question</span><span>Your Answer</span><span className="text-right">Pts</span>
                                </div>
                                {history.map((h, i) => {
                                    const c = h.isCorrect ? 'text-emerald-400' : h.isPartial ? 'text-amber-400' : 'text-rose-400';
                                    return (
                                        <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 px-3 py-2.5 border-t border-white/5 text-xs">
                                            <span className="text-slate-300 truncate">{String(h.card?.question || '').slice(0, 55)}</span>
                                            <span className="text-slate-400 truncate">
                                                {h.isTimeout ? <em className="text-slate-600">timeout</em> : h.userAnswer || <em className="text-slate-600">—</em>}
                                            </span>
                                            <span className={`font-semibold tabular-nums text-right ${c}`}>{h.pointsGained}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div className="w-full flex gap-3 pb-4">
                        <button onClick={() => setGameState('landing')}
                            className="flex-1 rounded-xl py-3 text-sm font-semibold text-white bg-gradient-to-r from-emerald-700 to-teal-700 hover:opacity-90 transition">
                            Play Again
                        </button>
                        <button onClick={onClose}
                            className="flex-1 rounded-xl py-3 text-sm font-medium text-slate-200 bg-slate-800 hover:bg-slate-700 ring-1 ring-white/10 transition">
                            Back to Study
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return null;
}
